/**
 * useCauseReport - Main data fetching hook for Cause Report
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { FilterConfig } from '../../../records/filter';
import type {
  CauseReportOverview,
  CauseReportCategories,
  UseCauseReportReturn
} from '../types';
import {
  causeReportKeys,
  CAUSE_REPORT_STALE_TIME
} from '../constants';
import { fetchWithAuth } from '../../../../../utils/api';
import { mapFiltersToApiParams } from '../utils';

interface TrendDataPointApi {
  date: string;
  value: number;
}

interface CauseReportApiResponse {
  overview: {
    total_records: number;
    total_records_delta: number;
    total_records_previous: number;
    total_records_trend: TrendDataPointApi[];
    avg_conduct_score: number;
    avg_conduct_score_delta: number;
    avg_conduct_score_previous: number;
    avg_conduct_score_trend: TrendDataPointApi[];
    period_start: string;
    period_end: string;
    previous_period_start: string;
    previous_period_end: string;
    last_updated: string;
  };
  categories: {
    categories: Array<{
      id: string;
      name: string;
      count: number;
      percentage: number;
    }>;
    total_records_with_root_cause: number;
  };
}

function transformOverview(data: CauseReportApiResponse['overview']): CauseReportOverview {
  return {
    totalRecords: data.total_records,
    totalRecordsDelta: data.total_records_delta,
    totalRecordsPrevious: data.total_records_previous,
    totalRecordsTrend: data.total_records_trend || [],
    avgConductScore: data.avg_conduct_score,
    avgConductScoreDelta: data.avg_conduct_score_delta,
    avgConductScorePrevious: data.avg_conduct_score_previous,
    avgConductScoreTrend: data.avg_conduct_score_trend || [],
    periodStart: data.period_start,
    periodEnd: data.period_end,
    previousPeriodStart: data.previous_period_start,
    previousPeriodEnd: data.previous_period_end,
    lastUpdated: data.last_updated,
  };
}

function transformCategories(data: CauseReportApiResponse['categories']): CauseReportCategories {
  return {
    categories: data.categories.map(cat => ({
      id: cat.id,
      name: cat.name,
      count: cat.count,
      percentage: cat.percentage,
    })),
    totalRecordsWithRootCause: data.total_records_with_root_cause,
  };
}

async function fetchCauseReportFoundation(
  datasetId: string,
  params: Record<string, string>
): Promise<CauseReportApiResponse> {
  const queryString = new URLSearchParams(params).toString();

  // Fetch both endpoints in parallel
  const [overviewRes, categoriesRes] = await Promise.all([
    fetchWithAuth(`/v1/datasets/${datasetId}/cause-report/overview?${queryString}`),
    fetchWithAuth(`/v1/datasets/${datasetId}/cause-report/categories?${queryString}`),
  ]);

  return {
    overview: overviewRes,
    categories: categoriesRes,
  };
}

export function useCauseReport(
  datasetId: string,
  filters: FilterConfig
): UseCauseReportReturn {
  const filterParams = useMemo(() => mapFiltersToApiParams(filters), [filters]);

  const query = useQuery({
    queryKey: causeReportKeys.foundation(datasetId, filterParams),
    queryFn: () => fetchCauseReportFoundation(datasetId, filterParams),
    select: (data: CauseReportApiResponse) => ({
      overview: transformOverview(data.overview),
      categories: transformCategories(data.categories),
    }),
    staleTime: CAUSE_REPORT_STALE_TIME,
    enabled: !!datasetId,
  });

  return {
    overview: query.data?.overview,
    categories: query.data?.categories,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
